/** @jsx jsx */
import { Box, EmptyState, EmptyStateListWhite, Pagination, Status } from "@eworkit/iris";
import { memo, useCallback, useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { useRouter } from "router";
import { jsx } from "theme-ui";

import { LoadingSpinner } from "@/components/Loading";
import { Section } from "@/components/Section";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM, MEDIA_SIZE_XS } from "@/core/media";
import { IURLSortConfig } from "@/core/url";
import {
  assignmentsListSearchParamsType,
  fetchAssignmentsList,
  IAssignment,
  IAssignmentsURLFilter,
  IPageableAssignment,
} from "@/data/assignments";
import { Col, Flex, Header, Row } from "@/eworkitComponents";
import { TPageableFetchFunction, useMetaPageableFetcher } from "@/hooks/http";
import { useDebounce } from "@/hooks/Timeout";
import { mapToQuery } from "@/infrastructure/Filter/service";
import { AssignmentsFilters } from "@/views/Assignments/ListPage/filters/AssignmentsFilters";
import { assignmentsFiltersMapper } from "@/views/Assignments/ListPage/filters/service";
import { useAssignmentFilters } from "@/views/Assignments/ListPage/filters/useAssignmentFilters";
import { Container } from "@/views/components/Container";
import { Infobox } from "@/views/components/Page";

import { Assignments } from "./components/Assignments";

export const AssignmentsListPage = memo(() => {
  const { t } = useTranslation(["public", "session"]);
  const { contextCompany } = useContext(UserContext);
  const isExtraSmallScreen = useMedia(MEDIA_SIZE_XS);
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const debounceTime = 600;

  const { searchParams: meta, replaceSearchParams: changeMeta } = useRouter({
    ensureSearchParams: assignmentsListSearchParamsType,
  });

  const fetchAssignments: TPageableFetchFunction<
    IAssignment,
    IPageableAssignment,
    IAssignmentsURLFilter,
    IURLSortConfig[]
  > = useCallback(
    (params, options) => {
      const { filtersConfig, ...restParams } = params;

      return fetchAssignmentsList(
        {
          ...restParams,
          companyId: contextCompany && contextCompany.id,
          ...mapToQuery(assignmentsFiltersMapper, filtersConfig || {}),
        },
        options,
      );
    },
    [contextCompany?.id],
  );

  const debouncedFilters = useDebounce(meta.filtersConfig, debounceTime);

  const mappedMeta = useMemo(() => {
    return {
      ...meta,
      filtersConfig: debouncedFilters,
    };
  }, [meta, debouncedFilters]);

  const { working, payload, changeFilter, changePage, changePageSize, counter } = useMetaPageableFetcher(
    fetchAssignments,
    {
      meta: mappedMeta,
      changeMeta,
    },
    [JSON.stringify(mappedMeta), changeMeta, contextCompany?.id],
  );

  const { handleFiltersChange, clearFilters } = useAssignmentFilters(meta.filtersConfig, changeFilter);

  const noAssignmentsFound = useMemo(() => {
    return !working && !payload?.content.length;
  }, [working, payload?.content]);

  const emptyFilters = useMemo(() => !Object.values(meta.filtersConfig).some(filter => !!filter), [meta.filtersConfig]);

  const noMatchingAssignmentsFound = useMemo(() => {
    return noAssignmentsFound && !emptyFilters;
  }, [noAssignmentsFound, emptyFilters]);

  const noActiveAssignments = !noAssignmentsFound || !emptyFilters;

  if (working && !counter) {
    return (
      <Flex width={12} align="center" justify="center" margin={["xl", false]}>
        <LoadingSpinner size="lg" variant="primary" />
      </Flex>
    );
  }

  return (
    <Container padding={["md", isSmallScreen ? false : "md"]}>
      <Helmet>
        <title>{t("public:META.ASSIGNMENTS_LIST.TITLE")}</title>
        <meta name="description" content={t("public:META.ASSIGNMENTS_LIST.DESCRIPTION")} />
      </Helmet>

      {!noMatchingAssignmentsFound && noAssignmentsFound ? (
        <Box sx={{ py: 120 }} px="sm">
          <EmptyState
            icon={<EmptyStateListWhite />}
            title={t("session:ASSIGNMENTS.LIST.NO_ITEMS.HEADING")}
            content={t("session:ASSIGNMENTS.LIST.NO_ITEMS.DESCRIPTION")}
          />
        </Box>
      ) : (
        <Row>
          <Col width={{ xxs: 12 }}>
            <Section
              background="background-card"
              padding={[isSmallScreen ? "md" : "lg"]}
              borderRadius={!isSmallScreen}
              shadow={isSmallScreen ? "none" : "default"}
              innerMargin="lg">
              {noActiveAssignments && (
                <Flex
                  direction={isExtraSmallScreen ? "column" : "row"}
                  width={12}
                  justify="space-between"
                  align="center"
                  innerMargin="md">
                  <Flex width={12} direction={"column"}>
                    <Box pb={"sm"}>
                      <Header size="md" weight="bold" sx={{ display: "flex" }}>
                        {t("session:ASSIGNMENTS.LIST.HEADER")}
                        <Status variant="secondary" sx={{ marginLeft: "xs" }}>
                          {payload?.totalElements ?? 0}
                        </Status>
                      </Header>
                    </Box>
                    <AssignmentsFilters
                      onFiltersUpdate={handleFiltersChange}
                      onFilterClear={clearFilters}
                      filtersValue={meta.filtersConfig}
                      applicationCount={payload?.totalElements}
                    />
                  </Flex>
                </Flex>
              )}

              <Flex width={12}>
                {working && (
                  <Flex width={12} align="center" justify="center" margin={["xl", false]}>
                    <LoadingSpinner size="lg" variant="primary" />
                  </Flex>
                )}

                {noMatchingAssignmentsFound && (
                  <Infobox
                    width={12}
                    padding={["xl", "md"]}
                    heading={t("session:ASSIGNMENTS.LIST.NO_MATCHING_ITEMS.HEADING")}
                    description={t("session:ASSIGNMENTS.LIST.NO_MATCHING_ITEMS.DESCRIPTION")}
                  />
                )}

                {!working && !!payload?.content.length && (
                  <Assignments assignments={payload.content.map(assignment => assignment.data)} />
                )}
              </Flex>
            </Section>

            {!working && (payload?.totalPages ?? 0) > 1 && (
              <Flex direction="column" width={12} padding={["lg", false, false, false]}>
                <Pagination
                  pagesCount={payload?.totalPages ?? 0}
                  activeIndex={payload?.number ?? 0}
                  onChangeIndex={changePage}
                  pageSize={payload?.size ?? 0}
                  showPageSizeSelect={false}
                  onChangePageSize={changePageSize}
                  resultsLabel={t("public:COMPONENTS.PAGINATION.RESULTS")}
                />
              </Flex>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
});
